import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import PublicGame from "@/pages/PublicGame";
import GameWaitingScreen from "@/components/GameWaitingScreen";
import InAppBrowserBlocker, {
  isInAppBrowser,
} from "@/components/InAppBrowserBlocker";

/**
 * Permanent merchant entry point (from the merchant QR code).
 * Fetches `/api/public/m/:slug`:
 *   - `status: "ok"` → render <PublicGame gameProp={game} /> (the active game)
 *   - `status: "no_active_game"` → show a polished "coming back soon" screen
 *   - 404 → generic not-found
 */
export default function MerchantPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/m/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 text-center"
        data-testid="merchant-404"
      >
        <div>
          <div className="text-3xl font-semibold tracking-tight">404</div>
          <p className="text-muted-foreground mt-2">{err}</p>
        </div>
      </div>
    );
  }

  if (isInAppBrowser()) {
    return (
      <InAppBrowserBlocker
        merchantName={data?.merchant?.name}
        merchantLogo={data?.merchant?.logo_url}
      />
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (data.status === "ok" && data.game) {
    return <PublicGame gameProp={data.game} />;
  }

  // "no_active_game" → friendly waiting state (texts customizable per merchant,
  // visuals of the next scheduled game when there is one)
  return <GameWaitingScreen merchant={data?.merchant || {}} upcoming={data?.upcoming || null} />;
}
